import React from 'react';
import { Row, Col, Button, Divider, Modal } from 'antd';
import styled from 'styled-components';
import Link from 'next/link';
import NImage from 'next/image';
import Image from 'components/Image';
import { SectionTitle } from 'components/Section';
import Avatar from 'components/Avatar';
import SocialLinks from 'components/SocialLinks';
import RodoText from 'components/RodoText';
import ContactForm from './ContactForm';
import Content from 'layout/Basic/Content';
import Sider from 'layout/Basic/Sider';
import Section from 'components/Section';
import { SkyscraperMobile } from 'components/Ads';
import DefaultBG from 'public/static/images/default_company_bg.png';

const BranchName = styled.h3`
    font-size: 18px;
    line-height: 1.44;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    text-align: right;
    margin: 0;
    color: ${({ theme }) => theme.colors.greyishBrownTwo};
`;

const BranchAddress = styled.p`
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    text-align: right;
    margin: 0;
    color: ${({ theme }) => theme.colors.primary};
`;

const BranchEmail = styled.p`
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    text-align: right;
    margin: 0;
    color: ${({ theme }) => theme.colors.primary};
`;

const BranchPhone = styled.p`
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    margin: 0;
    text-align: right;
    color: ${({ theme }) => theme.colors.greyishBrownTwo};
`;

const LogoContainer = styled.div`
    position: absolute;
    height: auto;
    top: 50%;
    left: 3%;
    border: 1px solid #b7cbd9;
    @media (max-width: 992px) {
        top: 55%;
        left: 50%;
        transform: translateX(-50%);
        > img {
            max-width: 140px;
        }
    }
    @media (max-width: 576px) {
        top: 15%;
    }
`;

const CompanyName = styled.h1`
    font-size: 32px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    color: ${({ theme }) => theme.colors.brownishGrey};
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const CompanyAddress = styled.h2`
    font-size: 20px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    color: ${({ theme }) => theme.colors.primary};
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const CompanyURL = styled.p`
    font-size: 20px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    a {
        color: ${({ theme }) => theme.colors.brownishGrey};
    }
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const PersonContainer = styled.div`
    text-align: right;
    word-wrap: break-word;
    color: ${({ theme }) => theme.colors.greyishBrownTwo};
`;

const PersonRow = styled.p`
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    margin: 0;
    a {
        color: ${({ theme }) => theme.colors.primary};
    }
`;

const TextBold = styled.p`
    font-size: 18px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.brownishGrey};
`;

const Text = styled.p`
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.brownishGrey};
`;

const RodoTextSpan = styled.span`
    font-size: 14px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.greyishBrown};
    > button {
        font-size: 14px;
        font-weight: 300;
    }
`;

const HeadingCol = styled(Col)`
    @media (max-width: 992px) {
        margin-bottom: 50px;
    }
`;

export interface Props extends Company {
    ads: PageAds;
}

const CompanyContact = ({
    name,
    seoSlug,
    url,
    logo,
    socialLinks,
    premium,
    premiumBackground,
    addresses,
    contactPersons,
    ads
}: Props): React.JSX.Element => {
    const showModal = () => {
        Modal.info({
            okText: 'Zamknij',
            width: '80%',
            title: 'Informacja o przetwarzaniu danych osobowych',
            content: <RodoText />
        });
    };

    const address = addresses && addresses.length > 0 ? addresses[0] : undefined;

    const { skyscraper } = ads;
    return (
        <>
            <Row style={{ position: 'relative' }}>
                <HeadingCol span={24}>
                    {premium && premiumBackground && (
                        <Image
                            priority
                            type="billboard"
                            width={1200}
                            height={303}
                            src={premiumBackground}
                            alt={name}
                        />
                    )}

                    {(!premium || !premiumBackground) && (
                        <NImage width={1200} height={303} src={DefaultBG} alt={name} />
                    )}

                    <LogoContainer>
                        <Image type="logo" width={270} height={270} src={logo} alt={name} />
                    </LogoContainer>
                </HeadingCol>
            </Row>
            <Section>
                <Col xs={{ span: 24, order: 2 }} lg={{ offset: 7, span: 11, order: 1 }}>
                    <Row>
                        <Col span={24}>
                            <CompanyName>{name}</CompanyName>
                            {address && (
                                <CompanyAddress>{`${address.street}, ${address.zip} ${address.city}, ${address.country}`}</CompanyAddress>
                            )}
                            {url && (
                                <CompanyURL>
                                    <a target="_blank" href={url} rel="noopener noreferrer">
                                        {url}
                                    </a>
                                </CompanyURL>
                            )}
                        </Col>
                        <Col span={24}>
                            <Divider
                                style={{
                                    margin: '5px 0 24px 0',
                                    width: '60%',
                                    minWidth: '60%'
                                }}
                            />
                            <Link href={`/katalog-firm/${seoSlug}`}>
                                <Button style={{ padding: '0 30px' }}>Wróć do szczegółów</Button>
                            </Link>
                        </Col>
                    </Row>
                </Col>
                <Col xs={{ span: 24, order: 1 }} lg={6}>
                    <Row justify="end" wrap>
                        <Col>
                            <SocialLinks {...socialLinks} />
                        </Col>
                    </Row>
                </Col>
            </Section>
            <Divider />
            <Row justify="center">
                <Col xs={24} sm={12}>
                    <SkyscraperMobile id={skyscraper[0]} margin="12px auto 12px auto" />
                </Col>
                <Col xs={24} sm={12}>
                    <SkyscraperMobile id={skyscraper[0]} margin="12px auto 12px auto" />
                </Col>
            </Row>
            {premium && (
                <>
                    <Section gutter={16}>
                        <Content>
                            <Row>
                                <Col xs={24} lg={9}>
                                    <SectionTitle title="Kontakt z firmą" />
                                </Col>
                                <Col xs={24} lg={15}>
                                    <Row>
                                        <Col span={24}>
                                            <TextBold>
                                                Chcesz skontaktować się z firmą? Wypełnij formularz.
                                            </TextBold>
                                            <Text>
                                                Twoje zapytanie zostanie wysłane jako e-mail do
                                                osoby kontaktowej.
                                            </Text>
                                        </Col>
                                        <Col span={24} style={{ paddingTop: 40 }}>
                                            <ContactForm seoSlug={seoSlug} />
                                        </Col>
                                        <Col>
                                            <RodoTextSpan>
                                                RAVEN MEDIA sp. z o.o. informuje, że będzie
                                                administratorem dotyczących Pani/Pana danych
                                                osobowych podanych w niniejszym formularzu i w toku
                                                realizacji zamówionych w formularzu usług.
                                                <Button
                                                    onClick={showModal}
                                                    type="link"
                                                    style={{ padding: '0 5px', height: 'auto' }}
                                                >
                                                    Więcej...
                                                </Button>
                                            </RodoTextSpan>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Content>
                        <Sider>
                            <Row gutter={[16, 32]}>
                                <Col span={24}>{/* <Rectangle /> CMS */}</Col>
                                <Col span={24}>{/* <HalfPage /> CMS */}</Col>
                            </Row>
                        </Sider>
                    </Section>
                    <Divider />
                </>
            )}
            <Section style={{ paddingTop: 35 }}>
                <Col span={24}>
                    <SectionTitle title="Dane adresowe" />
                </Col>
            </Section>
            <Row gutter={16} justify="center">
                <Content>
                    <Row gutter={16}>
                        {addresses.map((address) => (
                            <Col md={12} xl={8} key={address.name}>
                                {address.name && <BranchName>{address.name}</BranchName>}
                                <BranchAddress>
                                    {address.street}
                                    <br />
                                    {`${address.zip ? address.zip : ''} ${address.city}, ${
                                        address.country
                                    }`}
                                </BranchAddress>
                                {address.phoneNumber && (
                                    <BranchPhone>{`tel: ${address.phoneNumber}`}</BranchPhone>
                                )}
                                {address.phoneNumber2 && (
                                    <BranchPhone>{`tel: ${address.phoneNumber2}`}</BranchPhone>
                                )}
                                {address.fax && <BranchPhone>{`faks: ${address.fax}`}</BranchPhone>}
                                {address.email && (
                                    <BranchEmail>
                                        <a
                                            href={`mailto:${address.email}`}
                                            target="_blank"
                                            dangerouslySetInnerHTML={{
                                                __html: address.email.replace('@', '&#8203;@')
                                            }}
                                            rel="noreferrer"
                                        />
                                    </BranchEmail>
                                )}
                            </Col>
                        ))}
                    </Row>
                </Content>
            </Row>
            <Divider />
            {contactPersons.length > 0 && (
                <>
                    <Section>
                        <Col span={24}>
                            <SectionTitle title="Osoby kontaktowe" />
                        </Col>
                    </Section>
                    <Row gutter={16} justify="center">
                        <Content>
                            <Row gutter={16}>
                                {contactPersons.map((person) => {
                                    return (
                                        <Col md={12} xl={8} key={person.name}>
                                            <PersonContainer>
                                                <Avatar person={person} size={90} />
                                                <BranchName>{`${person.firstName} ${person.lastName}`}</BranchName>
                                                <PersonRow>{person.jobTitle}</PersonRow>
                                                <PersonRow>{person.department}</PersonRow>
                                                <PersonRow>tel: {person.phone}</PersonRow>
                                                {person.email && (
                                                    <PersonRow>
                                                        <a
                                                            href={`mailto:${person.email}`}
                                                            target="_blank"
                                                            dangerouslySetInnerHTML={{
                                                                __html: person.email.replace(
                                                                    '@',
                                                                    '&#8203;@'
                                                                )
                                                            }}
                                                            rel="noreferrer"
                                                        />
                                                    </PersonRow>
                                                )}
                                            </PersonContainer>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </Content>
                    </Row>
                </>
            )}
            <Row justify="center">
                <Col style={{ margin: '30px 0' }}>
                    {/* <Wideboard
                        id="1581938133657-0"
                        name="wideboard_standard"
                        targeting={{
                            key: 'wideboard_stand_nr',
                            value: ['1']
                        }}
                    />  CMS */}
                </Col>
            </Row>
        </>
    );
};

export default CompanyContact;
